import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import Seo from "@/components/Seo";
import PtfButton from "@/components/PtfButton";
import ShareButtons from "@/components/ShareButtons";
import { api, resolveImg } from "@/lib/api";

export default function StoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: s, isLoading, isError } = useQuery({
    queryKey: ["story", id],
    queryFn: async () => (await api.get(`/stories/${id}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="min-h-screen grid place-items-center bg-ptf-bg"><Loader2 className="h-8 w-8 animate-spin text-ptf-primary" /></div>;
  if (isError || !s) {
    return (
      <div className="min-h-screen grid place-items-center bg-ptf-bg text-center px-6">
        <div>
          <h1 className="font-display uppercase text-4xl text-ptf-primary">Story not found</h1>
          <div className="mt-6 flex justify-center"><PtfButton onClick={() => navigate("/stories")}>Back to Stories</PtfButton></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={s.title}
        path={`/stories/${id}`}
        description={s.excerpt || `A story from Planet Trotter Foundation.`}
        image={s.image ? resolveImg(s.image) : undefined}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: s.title,
            description: s.excerpt || undefined,
            author: s.author ? { "@type": "Person", name: s.author } : { "@type": "Organization", name: "Planet Trotter Foundation" },
            publisher: { "@type": "Organization", name: "Planet Trotter Foundation" },
            image: s.image ? resolveImg(s.image) : undefined,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://planettrotterfoundation.com/" },
              { "@type": "ListItem", position: 2, name: "Stories", item: "https://planettrotterfoundation.com/stories" },
              { "@type": "ListItem", position: 3, name: s.title },
            ],
          },
        ]}
      />

      <section className="relative bg-ptf-primary text-ptf-bg overflow-hidden grain">
        {s.image && (
          <>
            <img src={resolveImg(s.image)} alt={s.title} className="absolute inset-0 h-full w-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-ptf-primary/70 to-ptf-primary/40" />
          </>
        )}
        <div className="relative z-10 mx-auto max-w-[900px] px-5 sm:px-8 lg:px-12 pt-40 pb-20 md:pt-48 md:pb-24">
          <Link to="/stories" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-ptf-bg/70 hover:text-ptf-accent transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" /> All Stories
          </Link>
          <p className="text-[0.7rem] uppercase tracking-[0.24em] text-ptf-accent mb-5">{s.category}{s.date ? ` · ${s.date}` : ""}</p>
          <motion.h1
            data-testid="story-title"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-extrabold uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight"
          >
            {s.title}
          </motion.h1>
          {s.author && <p className="mt-6 text-ptf-bg/70">By {s.author}</p>}
        </div>
      </section>

      <section className="bg-ptf-bg py-16 md:py-24">
        <article className="mx-auto max-w-[720px] px-5 sm:px-8">
          {s.excerpt && <p className="font-serif-ed italic text-2xl text-ptf-secondary leading-snug mb-10">{s.excerpt}</p>}
          {(s.content || "").trimStart().startsWith("<") ? (
            <div data-testid="story-content" className="prose-ptf text-lg text-ptf-text/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.content) }} />
          ) : (
            <div data-testid="story-content" className="prose-ptf text-lg text-ptf-text/80 leading-relaxed whitespace-pre-line">{s.content}</div>
          )}
          <div className="mt-14 pt-10 border-t border-ptf-primary/12">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-10">
              <span className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-primary/50">Share this story</span>
              <ShareButtons title={s.title} />
            </div>
            <PtfButton to="/stories" variant="outline">More Stories</PtfButton>
          </div>
        </article>
      </section>
    </>
  );
}
